import { Outlet, NavLink } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Package,
  Users,
  DollarSign,
  BarChart3,
  Settings,
  Palette,
  UserCog,
  FileText,
  Truck,
  Gift,
  Tag,
  Monitor,
  HandCoins,
  Zap,
  ShoppingBag,
  UtensilsCrossed,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';

const navGroups = [
  {
    label: 'Vendas',
    items: [
      { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/pdv', label: 'Abrir PDV', icon: Monitor },
      { to: '/pdv/quick', label: 'Caixa Rápido', icon: Zap },
      { to: '/admin/bar-restaurant', label: 'Bar/Restaurante', icon: UtensilsCrossed },
      { to: '/admin/orders', label: 'Pedidos Online', icon: ShoppingBag },
      { to: '/admin/reports', label: 'Relatórios', icon: BarChart3 },
    ],
  },
  {
    label: 'Cadastros',
    items: [
      { to: '/admin/products', label: 'Produtos', icon: Package },
      { to: '/admin/customers', label: 'Clientes', icon: Users },
      { to: '/admin/suppliers', label: 'Fornecedores', icon: Truck },
    ],
  },
  {
    label: 'Financeiro',
    items: [
      { to: '/admin/financial', label: 'Financeiro', icon: DollarSign },
      { to: '/admin/credit', label: 'Crédito', icon: HandCoins },
      { to: '/admin/loyalty', label: 'Fidelidade', icon: Gift },
      { to: '/admin/promotions', label: 'Promoções', icon: Tag },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { to: '/admin/users', label: 'Usuários', icon: UserCog },
      { to: '/admin/audit', label: 'Auditoria', icon: FileText },
      { to: '/admin/settings', label: 'Configurações', icon: Settings },
      { to: '/admin/theme', label: 'Tema', icon: Palette },
    ],
  },
];

// Flat list for mobile bottom nav
const mobileNavItems = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/pdv', label: 'PDV', icon: Monitor },
  { to: '/admin/products', label: 'Produtos', icon: Package },
  { to: '/admin/financial', label: 'Financeiro', icon: DollarSign },
  { to: '/admin/settings', label: 'Config', icon: Settings },
];

export function AdminLayout() {
  const [logo, setLogo] = useState<string | null>(null);
  const { tenant } = useAuth();

  useEffect(() => {
    const savedLogo = localStorage.getItem('FluxoPDV_logo');
    if (savedLogo) setLogo(savedLogo);

    const interval = setInterval(() => {
      const current = localStorage.getItem('FluxoPDV_logo');
      if (current !== logo) setLogo(current);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card">
        {/* Logo + Establishment Name */}
        <div className="flex flex-col items-center gap-2 border-b border-border px-4 py-5">
          {logo ? (
            <img src={logo} alt="Logo" className="h-16 w-16 rounded-xl object-contain" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-primary/10">
              <Package className="h-8 w-8 text-primary" />
            </div>
          )}
          <span className="text-sm font-semibold text-foreground text-center">
            {tenant?.name || 'Meu Estabelecimento'}
          </span>
        </div>
        <nav className="flex-1 space-y-4 p-3 overflow-y-auto">
          {navGroups.map((group) => (
            <div key={group.label}>
              <span className="mb-1 block px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                {group.label}
              </span>
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary/10 text-primary border-l-2 border-primary'
                          : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                      )
                    }
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        {/* Logout button */}
        <div className="border-t border-border p-3">
          <button
            onClick={() => {
              localStorage.removeItem('peg_e_pag_tokens');
              localStorage.removeItem('peg_e_pag_user');
              localStorage.removeItem('peg_e_pag_tenant');
              window.location.href = '/login';
            }}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Sair
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pb-16 md:pb-0">
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 flex md:hidden items-center justify-around border-t border-border bg-card/95 backdrop-blur-sm px-2 py-2">
        {mobileNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center gap-0.5 rounded-lg px-2 py-1 text-xs transition-colors',
                isActive ? 'text-primary' : 'text-muted-foreground'
              )
            }
          >
            <item.icon className="h-5 w-5" />
            <span className="text-[11px]">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
